import { useMemo } from 'react';
import type { Order, OrderContractElement, Party } from '@hero/schema';
import { useCurrentUser } from '../user/useCurrentUser';

/**
 * Pure leg picker — given an Order and the viewer's identity-id set
 * (user / org / profile ids, all hex strings), returns the contract
 * leg the viewer is a party to (buyer OR seller).
 *
 * For a single-leg order there is at most one match. For a chained
 * order where the viewer is a broker (party to multiple legs) we
 * return the first match in `contracts[]` order, which by Phase-7
 * convention is the buyer-most leg — i.e. the viewer's "selling"
 * position upstream (closer to the customer). That's the leg whose
 * price the viewer charges to *their* counterparty, which is what we
 * want to surface as "their price" for display + edit.
 *
 * Returns `undefined` when the viewer is not a party to any leg
 * (e.g. an admin-panel super-admin browsing orders), or when the
 * order has no contracts yet (auto / own-fleet pre-allocation).
 *
 * Membership matches primary `_id` OR any `ids[]` entry — same rule as
 * backend `resolveViewerLeg` / order projection, so a legacy shipper
 * snapshot that only has `_id` still resolves.
 */
const partyInViewerSet = (party: Party | undefined, viewerIdSet: Set<string>): boolean => {
  if (!party) return false;
  if (party._id && viewerIdSet.has(party._id.toString())) return true;
  return (party.ids ?? []).some(e => viewerIdSet.has(e.id));
};

export const getViewerLeg = (
  order: Order | null | undefined,
  viewerIdSet: Set<string>,
): OrderContractElement | undefined => {
  if (!order) return undefined;
  const contracts = order.contracts ?? [];
  if (contracts.length === 0) return undefined;
  if (viewerIdSet.size === 0) return undefined;

  return contracts.find(
    leg => partyInViewerSet(leg.seller, viewerIdSet) || partyInViewerSet(leg.buyer, viewerIdSet),
  );
};

/**
 * Hook wrapper around {@link getViewerLeg} — pulls the viewer's
 * identity-id set from `useCurrentUser` so callers don't have to
 * thread `viewerOrgIds` / `userId` through props.
 *
 * Use this everywhere a surface needs to display or edit "the current
 * user's price" on an order. Centralising the leg-picking logic here
 * keeps every surface coherent — web's Summary tab, price column and
 * edit drawer, mobile's LoadCard and OrderTemplate — so the price the
 * viewer SEES on a list row is the price the editor LETS them change,
 * and is the price the backend will authorize them to write.
 *
 * Web and mobile each kept a byte-identical copy of this until it
 * moved here; `@hero/web-hooks` and `@hero/hooks` now re-export it, so
 * existing call sites on both platforms are unchanged.
 */
export const useViewerLeg = (
  order: Order | null | undefined,
): OrderContractElement | undefined => {
  const { user, orgId } = useCurrentUser();
  const viewerIdSet = useMemo(() => {
    const ids = new Set<string>();
    const userId = (user as { _id?: { toString(): string } } | undefined)?._id?.toString?.();
    if (userId) ids.add(userId);
    if (orgId) ids.add(orgId.toString());
    return ids;
  }, [user, orgId]);

  return useMemo(() => getViewerLeg(order, viewerIdSet), [order, viewerIdSet]);
};
